import { resolveMenu } from "./navigationRegistry";
import { useNavContext } from "../contexts/NavContext";

// Which tab the current path belongs to. A tab owns its own path and everything
// under it, so a page with sub-routes (a graduation plan detail, the Canvas
// setup/courses/logs children) keeps its section tab selected instead of
// dropping the whole strip to unselected. The trailing `/` boundary stops a tab
// from claiming a sibling that merely starts with its path, and the longest
// match wins so a nested tab beats the ancestor it sits under (Report Cards ->
// Dashboard on /report-cards/dashboard). Query strings are not part of a page's
// identity here: a tab that links with one (Grid Edit's ?type=) stays the active
// tab once other params are in play.
function activeKeyFor(links, currentPath) {
  if (!currentPath) return undefined;
  const matches = links
    .map((link) => ({
      link,
      path: (link.match || link.url || "").split("?")[0],
    }))
    .filter(
      ({ path }) =>
        path && (currentPath === path || currentPath.startsWith(`${path}/`))
    )
    .sort((a, b) => b.path.length - a.path.length);
  return matches.length ? matches[0].link.key : undefined;
}

// Build a PageHeader tab strip from a navigation-registry group id. Returns
// { tabs, activeKey } where `tabs` are ready descriptors ({ key, label, onClick,
// disabled }) permission-filtered by the registry (the same gating the nav menu
// applies), and `activeKey` is the child whose page the current path belongs to
// (see activeKeyFor above). Hand the result straight to PageHeader:
//
//   const { tabs, activeKey } = useNavMenu("report-cards");
//   <PageHeader title="Report Cards" tabs={tabs} activeTab={activeKey} />
//
// Requires a NavProvider ancestor (mounted in the School layout). Without one it
// returns an empty strip rather than throwing, so a header outside that scope
// simply renders no tabs.
export default function useNavMenu(id) {
  const nav = useNavContext();
  const links = nav ? resolveMenu(id, nav.ctx) : [];
  const tabs = links.map((link) => ({
    key: link.key,
    label: link.label,
    disabled: link.disabled,
    onClick:
      link.disabled || !link.url
        ? undefined
        : () => nav.navigate(link.url, link.v1),
  }));
  return { tabs, activeKey: activeKeyFor(links, nav?.currentPath) };
}
